// Global Instructions Rule Applied!
// Detail shape of GET /journal-entries/:id and POST /journal-entries
// (je__entry_detail): header + lines + totals. Keys are raw column names
// (to_jsonb of the row) plus joined labels, so the row schemas are
// passthrough while pinning the fields the UI consumes.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const JournalEntryHeaderSchema = z
  .object({
    id: z.string(),
    fund_id: z.string(),
    entry_date: z.string(),
    description: z.string().nullish(),
    status_id: z.number().nullish(),
    fund_transaction_id: z.string().nullish(),
    asset_transaction_id: z.string().nullish(),
    trashed: z.boolean().nullish(),
    fund_name: z.string().nullish(),
    status_label: z.string().nullish(),
    fund_transaction_label: z.string().nullish(),
    asset_transaction_label: z.string().nullish()
  })
  .passthrough()

export const JournalEntryLineSchema = z
  .object({
    id: z.string(),
    journal_date: z.string().nullish(),
    item_id: z.string().nullish(),
    item_label: z.string().nullish(),
    debit_credit_id: z.string(),
    amount: z.number(),
    description: z.string().nullish(),
    journal_entry_id: z.string().nullish(),
    fund_commitment_id: z.string().nullish(),
    // the line's LP as the fund ledger names it: fund_commitments__label as
    // at the line's date (the holder then, not after a later transfer)
    investor_name: z.string().nullish()
  })
  .passthrough()

export const JournalEntryTotalsSchema = z
  .object({
    debits: z.number(),
    credits: z.number(),
    difference: z.number()
  })
  .passthrough()

export const JournalEntryDetailSchema = z
  .object({
    entry: JournalEntryHeaderSchema,
    lines: z.array(JournalEntryLineSchema),
    totals: JournalEntryTotalsSchema
  })
  .strict()

export type JournalEntryHeader = z.infer<typeof JournalEntryHeaderSchema>
export type JournalEntryLine = z.infer<typeof JournalEntryLineSchema>
export type JournalEntryTotals = z.infer<typeof JournalEntryTotalsSchema>
export type JournalEntryDetail = z.infer<typeof JournalEntryDetailSchema>

export function validateJournalEntryDetail(data: unknown): JournalEntryDetail {
  try {
    return validateWithZodSchema(JournalEntryDetailSchema, data, 'journal entry detail')
  } catch (error) {
    throw new Error(`Journal entry detail validation failed: ${error}`)
  }
}

/** Save payload sent to POST /journal-entries (je__save_entry). */
export interface JournalEntrySavePayload {
  id?: string
  fund_id?: string
  entry_date?: string
  description?: string | null
  fund_transaction_id?: string | null
  asset_transaction_id?: string | null
  lines?: Array<{
    id?: string
    acc_item_id: string
    debit_credit_id: 'dr' | 'cr'
    amount: number
    description?: string | null
    fund_commitment_id?: string | null
  }>
}

/** Option lists from GET /journal-entries/options?fund_id=. */
export interface JournalEntryOptions {
  lps: Array<{ value: string; label: string }>
  fund_transactions: Array<{ value: string; label: string }>
  asset_transactions: Array<{ value: string; label: string }>
}
